import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton, 
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Alert,
  Slider,
  TextField
} from '@mui/material';
import Draggable from 'react-draggable';
import { 
  Close as CloseIcon, 
  Add as AddIcon, 
  Save as SaveIcon, 
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  RestartAlt as RestartAltIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  WrapText as WrapTextIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import { supabase } from '../../../../supabaseClient';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const TemplateMapper = ({ open, onClose, availableFields, onSave }) => {
  const [step, setStep] = useState('select-form'); // select-form, upload-or-map, mapping
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [existingTemplate, setExistingTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [pdfImage, setPdfImage] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 }); 
  const [fields, setFields] = useState([]);
  const [fontSize, setFontSize] = useState(12);
  const [customLabel, setCustomLabel] = useState('');
  const [customValue, setCustomValue] = useState('');
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep('select-form');
      setSelectedFormType(null);
      setExistingTemplate(null);
      setPdfImage(null);
      setFields([]);
      setFontSize(12);
      setCustomLabel('');
      setCustomValue('');
      setError(null);
    }
  }, [open]);

  const handleSelectForm = async (type) => {
    setSelectedFormType(type);
    setLoading(true);
    setError(null);
    try {
      // Check for existing template
      const { data, error } = await supabase
        .from('bir_form_templates')
        .select('*')
        .eq('form_type', type)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 is "No rows found"
        throw error;
      }

      setExistingTemplate(data);
      setStep('upload-or-map');
    } catch (err) {
      console.error('Error fetching template:', err);
      setError('Failed to fetch template info.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    try {
      let fileToUpload = file;
      let fileName = `${selectedFormType}_template_${Date.now()}.pdf`;
      let contentType = 'application/pdf';

      // Convert Image to PDF if necessary
      if (file.type.startsWith('image/')) {
        console.log('Converting Image to PDF...');
        const imageBytes = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.create();
        
        let image;
        if (file.type === 'image/jpeg') {
            image = await pdfDoc.embedJpg(imageBytes);
        } else {
            image = await pdfDoc.embedPng(imageBytes);
        }

        const page = pdfDoc.addPage([image.width, image.height]);
        page.drawImage(image, {
            x: 0,
            y: 0,
            width: image.width,
            height: image.height,
        });

        const pdfBytes = await pdfDoc.save();
        console.log('Generated PDF size:', pdfBytes.byteLength);
        fileToUpload = new Blob([pdfBytes], { type: 'application/pdf' });
      }
      
      console.log(`Uploading ${fileName}, Size: ${fileToUpload.size}, Type: ${fileToUpload.type}`);
      
      // Upload to Storage with minimal options to avoid network issues
      // Remove upsert and cacheControl to simplify the request
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('bir-templates')
        .upload(fileName, fileToUpload);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('bir-templates')
        .getPublicUrl(fileName);

      // Save/Update DB
      const { data: dbData, error: dbError } = await supabase
        .from('bir_form_templates')
        .upsert({
          form_type: selectedFormType,
          file_url: publicUrl,
          updated_at: new Date()
        })
        .select()
        .single();

      if (dbError) throw dbError;

      setExistingTemplate(dbData);
      // Automatically proceed to mapping? Or stay on options?
      // Let's load the mapping view
      await loadTemplateForMapping(fileToUpload, dbData.mappings);
    } catch (err) {
      console.error('Error uploading template:', err);
      setError('Failed to upload template: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUseExisting = async () => {
    if (!existingTemplate?.file_url) return;
    setLoading(true);
    try {
      // Fetch the PDF file
      const response = await fetch(existingTemplate.file_url);
      const blob = await response.blob();
      await loadTemplateForMapping(blob, existingTemplate.mappings);
    } catch (err) {
      console.error('Error loading existing template:', err);
      setError('Failed to load existing template.');
      setLoading(false);
    }
  };

  const loadTemplateForMapping = async (fileBlob, savedMappings) => {
    try {
      await renderPdfToImage(fileBlob);
      
      // Load mappings if any
      if (savedMappings) {
        if (Array.isArray(savedMappings)) {
             setFields(savedMappings);
        } else {
            const loadedFields = [];

            Object.entries(savedMappings).forEach(([key, value]) => {
                if (key === '_custom' && Array.isArray(value)) {
                    value.forEach((item) => {
                        loadedFields.push({
                            id: `custom_${makeId()}`,
                            type: 'custom',
                            label: item?.label || '',
                            value: item?.value || '',
                            x: Math.max(0, item?.uiX || 50),
                            y: Math.max(0, item?.uiY || 50),
                            width: item?.uiWidth || 150,
                            alignment: item?.alignment || 'left',
                            fontSize: item?.size || fontSize,
                            color: item?.color || '#000000',
                            wrapText: item?.wrapText || false,
                            fontWeight: item?.fontWeight || 'bold',
                            fontStyle: item?.fontStyle || 'normal'
                        });
                    });
                    return;
                }

                if (Array.isArray(value)) {
                    value.forEach((pos, idx) => {
                        loadedFields.push({
                            id: `${key}_${idx}_${makeId()}`,
                            type: 'mapped',
                            key,
                            x: Math.max(0, pos?.uiX || 50),
                            y: Math.max(0, pos?.uiY || 50),
                            width: pos?.uiWidth || 150,
                            alignment: pos?.alignment || 'left',
                            fontSize: pos?.size || fontSize,
                            color: pos?.color || '#000000',
                            wrapText: pos?.wrapText || false,
                            fontWeight: pos?.fontWeight || 'bold',
                            fontStyle: pos?.fontStyle || 'normal'
                        });
                    });
                    return;
                }

                if (value && typeof value === 'object') {
                    loadedFields.push({
                        id: `${key}_0_${makeId()}`,
                        type: 'mapped',
                        key,
                        x: Math.max(0, value.uiX || 50),
                        y: Math.max(0, value.uiY || 50),
                        width: value.uiWidth || 150,
                        alignment: value.alignment || 'left',
                        fontSize: value.size || fontSize,
                        color: value.color || '#000000',
                        wrapText: value.wrapText || false,
                        fontWeight: value.fontWeight || 'bold',
                        fontStyle: value.fontStyle || 'normal'
                    });
                }
            });

            const firstMapped = Object.entries(savedMappings).find(([k, v]) => k !== '_custom' && v && typeof v === 'object');
            if (firstMapped) {
                const v = firstMapped[1];
                const sample = Array.isArray(v) ? v[0] : v;
                if (sample?.size) setFontSize(sample.size);
            }

            setFields(loadedFields);
        }
      } else {
          setFields([]);
      }

      setStep('mapping');
    } catch (err) {
      console.error('Error rendering PDF:', err);
      setError('Failed to render PDF.');
    } finally {
      setLoading(false);
    }
  };

  const renderPdfToImage = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument(arrayBuffer);
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale: 1.5 });
    setPdfDimensions({ width: viewport.width / 1.5, height: viewport.height / 1.5 });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({ canvasContext: context, viewport: viewport }).promise;
    setPdfImage(canvas.toDataURL());
  };

  const handleAddField = (fieldKey) => {
    setFields(prev => [
      ...prev,
      {
        id: `${fieldKey}_${makeId()}`,
        type: 'mapped',
        key: fieldKey,
        x: 50,
        y: 50,
        width: 150,
        alignment: 'left',
        fontSize,
        color: '#000000',
        fontWeight: 'bold',
        fontStyle: 'normal'
      }
    ]);
  };

  const handleRemoveField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
  };

  const handleAlignmentChange = (id, newAlignment) => {
    setFields(prev => prev.map(f => {
        if (f.id === id) {
            return { ...f, alignment: newAlignment };
        }
        return f;
    }));
  };

  const handleDragStop = (id, e, data) => {
    console.log(`Field ${id} dragged to: ${data.x}, ${data.y}`);
    setFields(prev => prev.map(f => {
      if (f.id === id) {
        return { ...f, x: data.x, y: data.y };
      }
      return f;
    }));
  };

  const handleFieldStyleChange = (id, changes) => {
    setFields(prev =>
      prev.map(f => (f.id === id ? { ...f, ...changes } : f))
    );
  };

  const handleSaveMapping = async () => {
    if (!imgRef.current || !pdfDimensions.width) return;

    // Check for potential unmapped fields (stuck at y=0)
    const zeroYFields = fields.filter(f => f.y === 0);
    if (zeroYFields.length > 0) {
        console.warn('Warning: Some fields are at Y=0 (Top of page). User might not have moved them.', zeroYFields.map(f => f.id));
        alert(`Warning: ${zeroYFields.length} fields are at the very top edge (Y=0). Please ensure you have dragged them to the correct positions on the form.`);
    }

    setLoading(true);
    try {
        // Use the actual image dimensions for scaling calculation
        // This ensures coordinates are relative to the image, not the container which might have extra space
        const containerW = imgRef.current.offsetWidth;
        const containerH = imgRef.current.offsetHeight;

        console.log('--- Saving Mapping Debug ---');
        console.log('PDF Original Dimensions:', pdfDimensions);
        console.log('Image Rendered Dimensions:', { width: containerW, height: containerH });

        const scaleX = pdfDimensions.width / containerW;
        const scaleY = pdfDimensions.height / containerH;
        
        console.log('Calculated Scale:', { scaleX, scaleY });

        const mapping = {};
        const custom = [];
        
        fields.forEach(field => {
            // Sanitize coordinates to prevent negative values (off-screen/invisible)
            const safeX = Math.max(0, field.x);
            const safeY = Math.max(0, field.y);

            const pdfX = safeX * scaleX;
            const fieldFontSize = field.fontSize || fontSize;
            
            // PDF Coordinate System: (0,0) is usually BOTTOM-LEFT.
            // UI Coordinate System: (0,0) is TOP-LEFT.
            const pdfY = pdfDimensions.height - (safeY * scaleY) - fieldFontSize;

            console.log(`Field ${field.id}: UI(${safeX}, ${safeY}) -> PDF(${pdfX}, ${pdfY})`);

            const entry = {
                x: pdfX,
                y: pdfY,
                size: fieldFontSize,
                uiX: safeX,
                uiY: safeY,
                alignment: field.alignment || 'left',
                uiWidth: field.width || 150,
                width: (field.width || 150) * scaleX,
                color: field.color || '#000000',
                wrapText: field.wrapText || false,
                fontWeight: field.fontWeight || 'bold',
                fontStyle: field.fontStyle || 'normal'
            };

            if (field.type === 'custom') {
                custom.push({
                    label: field.label || '',
                    value: field.value || '',
                    ...entry
                });
            } else {
                const key = field.key || field.id;
                if (!mapping[key]) mapping[key] = [];
                mapping[key].push(entry);
            }
        });

        if (custom.length > 0) mapping._custom = custom;

        // Save to DB
        const { error } = await supabase
            .from('bir_form_templates')
            .update({
                mappings: mapping // storing the mapping object. We could also store `fields` array to be exact.
            })
            .eq('form_type', selectedFormType);

        if (error) throw error;

        if (onSave) onSave(selectedFormType, mapping, existingTemplate?.file_url); // Notify parent with URL
        onClose();
    } catch (err) {
        console.error('Error saving mapping:', err);
        setError('Failed to save mapping.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Template Mapper {selectedFormType && `- Form ${selectedFormType}`}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        )}
        
        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && step === 'select-form' && (
            <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
                <Grid item xs={12} sm={5}>
                    <Card>
                        <CardActionArea onClick={() => handleSelectForm('2307')} sx={{ height: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <DescriptionIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h5">BIR Form 2307</Typography>
                            <Typography variant="body2" color="text.secondary">Certificate of Creditable Tax Withheld at Source</Typography>
                        </CardActionArea>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={5}>
                    <Card>
                        <CardActionArea onClick={() => handleSelectForm('2306')} sx={{ height: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <DescriptionIcon sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                            <Typography variant="h5">BIR Form 2306</Typography>
                            <Typography variant="body2" color="text.secondary">Certificate of Final Tax Withheld at Source</Typography>
                        </CardActionArea>
                    </Card>
                </Grid>
            </Grid>
        )}

        {!loading && step === 'upload-or-map' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
                <Typography variant="h6">
                    {existingTemplate ? 'Template Found' : 'No Template Found'}
                </Typography>
                
                {existingTemplate && (
                    <Button 
                        variant="contained" 
                        size="large" 
                        startIcon={<EditIcon />}
                        onClick={handleUseExisting}
                    >
                        Map Existing Template
                    </Button>
                )}

                <Button
                    component="label"
                    variant="outlined"
                    size="large"
                    startIcon={<CloudUploadIcon />}
                >
                    Upload {existingTemplate ? 'New' : ''} Template PDF
                    <input
                        type="file"
                        hidden
                        accept="application/pdf,image/png,image/jpeg"
                        onChange={handleUpload}
                    />
                </Button>
                
                <Typography variant="caption" color="text.secondary">
                    Supports PDF, PNG, or JPG (Images will be converted to PDF automatically)
                </Typography>
                
                <Button onClick={() => setStep('select-form')}>Back</Button>
            </Box>
        )}

        {!loading && step === 'mapping' && (
            <Box sx={{ display: 'flex', height: '70vh', gap: 2 }}>
                {/* Sidebar - Fields */}
                <Paper sx={{ width: 250, p: 2, overflowY: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Available Fields</Typography>
                        <IconButton size="small" onClick={() => {
                            if (window.confirm('Reset all fields to default positions?')) {
                                setFields([]);
                            }
                        }} title="Reset All Mappings">
                            <RestartAltIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                            Custom Field
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            label="Label"
                            value={customLabel}
                            onChange={(e) => setCustomLabel(e.target.value)}
                            sx={{ mb: 1 }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Value"
                            value={customValue}
                            onChange={(e) => setCustomValue(e.target.value)}
                            sx={{ mb: 1 }}
                        />
                        <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                const label = customLabel.trim();
                                const value = customValue;
                                if (!label && !value) return;
                                setFields(prev => ([
                                    ...prev,
                                    {
                                        id: `custom_${makeId()}`,
                                        type: 'custom',
                                        label,
                                        value,
                                        x: 50,
                                        y: 50,
                                        width: 150,
                                        alignment: 'left',
                                        fontSize,
                                        color: '#000000',
                                        fontWeight: 'bold',
                                        fontStyle: 'normal'
                                    }
                                ]));
                                setCustomLabel('');
                                setCustomValue('');
                            }}
                            disabled={!customLabel.trim() && !customValue}
                        >
                            Add Custom
                        </Button>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography id="font-size-slider" gutterBottom>
                            Font Size: {fontSize}px
                        </Typography>
                        <Slider
                            value={fontSize}
                            onChange={(e, newValue) => setFontSize(newValue)}
                            valueLabelDisplay="auto"
                            step={1}
                            marks
                            min={8}
                            max={24}
                            size="small"
                        />
                    </Box>

                    {availableFields.map(field => {
                        const count = fields.filter(f => (f.type === 'mapped' || !f.type) && (f.key || f.id) === field.key).length;
                        return (
                            <Button
                                key={field.key}
                                variant={count > 0 ? "contained" : "outlined"}
                                color={count > 0 ? "success" : "primary"}
                                fullWidth
                                size="small"
                                sx={{ mb: 1, justifyContent: 'flex-start', textAlign: 'left' }}
                                onClick={() => handleAddField(field.key)}
                                startIcon={<AddIcon />}
                            >
                                {field.label}{count > 0 ? ` (${count})` : ''}
                            </Button>
                        );
                    })}
                </Paper>

                {/* Main Canvas - PDF Image */}
                <Box 
                    sx={{ 
                        flex: 1, 
                        bgcolor: '#f0f0f0', 
                        overflow: 'auto', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'flex-start',
                        p: 2
                    }}
                >
                    {pdfImage ? (
                        <div 
                            ref={containerRef}
                            style={{ 
                                position: 'relative', 
                                border: '1px solid #ccc',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                display: 'inline-block'
                            }}
                        >
                            <img 
                                ref={imgRef}
                                src={pdfImage} 
                                alt="Template" 
                                style={{ display: 'block', maxWidth: '100%', maxHeight: 'none' }} 
                                draggable={false}
                            />
                            
                            {fields.map(field => {
                                // Create a ref for each draggable item to avoid findDOMNode warning
                                const nodeRef = React.createRef();
                                const key = field.type === 'custom' ? '' : (field.key || field.id);
                                const label = field.type === 'custom'
                                    ? (field.label || 'Custom')
                                    : (availableFields.find(af => af.key === key)?.label || key);
                                return (
                                    <Draggable
                                        key={field.id}
                                        nodeRef={nodeRef}
                                        // bounds="parent" // Removed to prevent potential constraining issues during debug
                                        defaultPosition={{ x: field.x, y: field.y }}
                                        onStop={(e, data) => handleDragStop(field.id, e, data)}
                                    >
                                        <div
                                            ref={nodeRef}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: field.width || 150,
                                                cursor: 'move',
                                                backgroundColor: 'rgba(255, 255, 0, 0.3)',
                                                border: '1px dashed red',
                                                padding: '2px 4px',
                                                fontSize: `${field.fontSize || fontSize}px`,
                                                fontWeight: field.fontWeight || 'bold',
                                                fontStyle: field.fontStyle || 'normal',
                                                whiteSpace: field.wrapText ? 'normal' : 'nowrap',
                                                lineHeight: field.wrapText ? 1 : 'normal',
                                                color: field.color || '#000000',
                                                zIndex: 10,
                                                textAlign: field.alignment || 'left'
                                            }}
                                            title={`x: ${field.x}, y: ${field.y}`}
                                        >
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center', 
                                                marginBottom: 2,
                                                borderBottom: '1px dotted #ccc',
                                                paddingBottom: 2
                                            }}>
                                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                    <BoldIcon
                                                        fontSize="inherit"
                                                        sx={{ cursor: 'pointer', color: (field.fontWeight === 'bold' || !field.fontWeight) ? 'black' : 'gray', width: 14, height: 14 }}
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            const newWeight = (field.fontWeight === 'bold' || !field.fontWeight) ? 'normal' : 'bold';
                                                            handleFieldStyleChange(field.id, { fontWeight: newWeight }); 
                                                        }}
                                                        title="Bold"
                                                    />
                                                    <ItalicIcon
                                                        fontSize="inherit"
                                                        sx={{ cursor: 'pointer', color: field.fontStyle === 'italic' ? 'black' : 'gray', width: 14, height: 14 }}
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            const newStyle = field.fontStyle === 'italic' ? 'normal' : 'italic';
                                                            handleFieldStyleChange(field.id, { fontStyle: newStyle }); 
                                                        }}
                                                        title="Italic"
                                                    />
                                                    <AlignLeftIcon 
                                                        fontSize="inherit" 
                                                        sx={{ cursor: 'pointer', color: field.alignment === 'left' ? 'blue' : 'gray', width: 14, height: 14 }}
                                                        onClick={(e) => { e.stopPropagation(); handleAlignmentChange(field.id, 'left'); }}
                                                    />
                                                    <AlignCenterIcon 
                                                        fontSize="inherit" 
                                                        sx={{ cursor: 'pointer', color: field.alignment === 'center' ? 'blue' : 'gray', width: 14, height: 14 }}
                                                        onClick={(e) => { e.stopPropagation(); handleAlignmentChange(field.id, 'center'); }}
                                                    />
                                                    <AlignRightIcon 
                                                        fontSize="inherit" 
                                                        sx={{ cursor: 'pointer', color: field.alignment === 'right' ? 'blue' : 'gray', width: 14, height: 14 }}
                                                        onClick={(e) => { e.stopPropagation(); handleAlignmentChange(field.id, 'right'); }}
                                                    />
                                                    <WrapTextIcon
                                                        fontSize="inherit"
                                                        sx={{ cursor: 'pointer', color: field.wrapText ? 'blue' : 'gray', width: 14, height: 14, ml: 0.5 }}
                                                        onClick={(e) => { e.stopPropagation(); handleFieldStyleChange(field.id, { wrapText: !field.wrapText }); }}
                                                        title="Wrap Text"
                                                    />
                                                    <input
                                                        type="number"
                                                        min={10}
                                                        value={field.width || 150}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value, 10);
                                                            if (!isNaN(val)) {
                                                                handleFieldStyleChange(field.id, { width: val });
                                                            }
                                                        }}
                                                        style={{ width: 35, fontSize: 10, padding: 0, marginLeft: 2 }}
                                                        title="Width"
                                                    />
                                                    <input
                                                        type="number"
                                                        min={6}
                                                        max={24}
                                                        value={field.fontSize || fontSize}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value, 10);
                                                            if (!isNaN(val)) {
                                                                handleFieldStyleChange(field.id, { fontSize: val });
                                                            }
                                                        }}
                                                        style={{
                                                            width: 40,
                                                            fontSize: 10,
                                                            padding: 0,
                                                            marginLeft: 4
                                                        }}
                                                    />
                                                    <input
                                                        type="color"
                                                        value={field.color || '#000000'}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => {
                                                            handleFieldStyleChange(field.id, { color: e.target.value || '#000000' });
                                                        }}
                                                        style={{
                                                            width: 26,
                                                            height: 18,
                                                            padding: 0,
                                                            border: 'none',
                                                            background: 'transparent',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                </div>
                                                <span
                                                    style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: 14, lineHeight: 1 }}
                                                    onClick={(e) => { e.stopPropagation(); handleRemoveField(field.id); }}
                                                >
                                                    ×
                                                </span>
                                            </div>
                                            
                                            <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {label}
                                            </div>

                                            {field.type === 'custom' && field.value !== undefined && field.value !== '' && (
                                                <span style={{ fontSize: '10px' }}>
                                                    {String(field.value)}
                                                    <br/>
                                                </span>
                                            )}
                                            <span style={{fontSize: '9px'}}>({Math.round(field.x)}, {Math.round(field.y)})</span>
                                        </div>
                                    </Draggable>
                                );
                            })}
                        </div>
                    ) : (
                        <Typography>Loading Template...</Typography>
                    )}
                </Box>
            </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'mapping' && (
            <>
                <Button onClick={() => setStep('upload-or-map')}>Back</Button>
                <Button variant="contained" onClick={handleSaveMapping} startIcon={<SaveIcon />}>Save Mapping</Button>
            </>
        )}
        {step !== 'mapping' && <Button onClick={onClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateMapper;
